import React from "react";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { TrendingUp, PieChart, Landmark, ShieldCheck, Download, ChevronRight } from "lucide-react";
import { cn } from "../../../lib/utils";

const FinancialsTab: React.FC = () => {
    return (
        <div className="px-4 pb-24 space-y-6 mt-6">
            {/* Financial Overview Cards */}
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-3">
                <Card className="min-w-[160px] flex-1 p-4 bg-card rounded-2xl border border-border shadow-sm relative overflow-hidden group">
                    <div className="absolute right-0 top-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Landmark className="text-5xl text-primary" />
                    </div>
                    <div className="flex flex-col h-full justify-between relative z-10">
                        <div>
                            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Annual Revenue</div>
                            <div className="text-xl font-extrabold text-foreground">$840K</div>
                        </div>
                        <div className="mt-3 flex items-center gap-1 text-emerald-600 dark:text-emerald-400 text-xs font-bold bg-emerald-100 dark:bg-emerald-900/20 w-fit px-2 py-1 rounded">
                            <TrendingUp className="w-3 h-3" /> 18.5% YoY
                        </div>
                    </div>
                </Card>

                <Card className="min-w-[160px] flex-1 p-4 bg-card rounded-2xl border border-border shadow-sm relative overflow-hidden">
                    <div className="flex flex-col h-full justify-between z-10 relative text-foreground">
                        <div>
                            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">5yr Projection</div>
                            <div className="text-xl font-extrabold">$3.2M</div>
                        </div>
                        <div className="mt-3 h-6 flex items-end gap-0.5">
                            <div className="w-1/5 bg-primary/20 h-[30%] rounded-t-sm"></div>
                            <div className="w-1/5 bg-primary/40 h-[45%] rounded-t-sm"></div>
                            <div className="w-1/5 bg-primary/60 h-[60%] rounded-t-sm"></div>
                            <div className="w-1/5 bg-primary/80 h-[75%] rounded-t-sm"></div>
                            <div className="w-1/5 bg-primary h-[90%] rounded-t-sm"></div>
                        </div>
                    </div>
                </Card>

                <Card className="min-w-[160px] flex-1 p-4 bg-card rounded-2xl border border-border shadow-sm relative overflow-hidden">
                    <div className="flex flex-col h-full justify-between relative z-10">
                        <div>
                            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1">Market Multiplier</div>
                            <div className="text-xl font-extrabold text-foreground">4.2x</div>
                        </div>
                        <div className="mt-3 text-xs text-muted-foreground">
                            Avg. Sector: <span className="font-bold text-foreground">3.8x</span>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Patent Highlight */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-xl p-5 text-white shadow-lg flex items-center justify-between relative overflow-hidden">
                <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
                <div className="absolute -left-6 -bottom-6 w-24 h-24 bg-indigo-400/20 rounded-full blur-2xl"></div>
                <div className="flex items-center gap-4 relative z-10">
                    <div className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/20">
                        <ShieldCheck className="text-white w-6 h-6" />
                    </div>
                    <div>
                        <h3 className="font-bold text-lg leading-none">Patent Granted</h3>
                        <p className="text-indigo-200 text-xs mt-1 font-medium">US Patent #9,231,452 • Solar Tracking</p>
                    </div>
                </div>
                <ChevronRight className="text-white/50 w-6 h-6 z-10" />
            </div>

            {/* Main Chart Card */}
            <Card className="p-6 border border-border shadow-sm">
                <div className="flex justify-between items-start mb-6">
                    <div>
                        <h3 className="font-bold text-lg text-foreground">Financial Performance</h3>
                        <p className="text-xs text-muted-foreground">Revenue & EBITDA Growth (Last 5 Years)</p>
                    </div>
                    <Button variant="ghost" size="icon" className="text-muted-foreground w-8 h-8 rounded-full hover:bg-muted/50">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                        </svg>
                    </Button>
                </div>

                {/* SVG Line Chart */}
                <div className="relative w-full h-64 mt-4 select-none">
                    <svg viewBox="0 0 1000 320" className="w-full h-full overflow-visible">
                        {/* Gradients */}
                        <defs>
                            <linearGradient id="salesLineGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity="0.15" />
                                <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                            </linearGradient>
                        </defs>

                        {/* Y-Axis Grid Lines & Labels */}
                        {[2000, 1800, 1600, 1400, 1200, 1000, 800, 600].map((val, idx) => {
                            const y = 20 + idx * 37.14;
                            return (
                                <g key={val} className="animate-in fade-in duration-500 delay-[idx*100ms]">
                                    <line x1="50" y1={y} x2="950" y2={y} stroke="currentColor" className="text-muted/30" strokeDasharray="4 4" />
                                    <text x="40" y={y + 4} textAnchor="end" className="text-[14px] fill-muted-foreground font-medium">
                                        ${val}k
                                    </text>
                                </g>
                            );
                        })}

                        {/* Area Fill */}
                        <path
                            d="M50,260 L275,236 L500,197 L725,136 L950,48 V280 H50 Z"
                            fill="url(#salesLineGradient)"
                            className="animate-in fade-in duration-1000 delay-700"
                        />

                        {/* Chart Line */}
                        <path
                            d="M50,260 L275,236 L500,197 L725,136 L950,48"
                            fill="none"
                            stroke="hsl(var(--primary))"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="animate-[drawLines_1.5s_ease-out_forwards]"
                            style={{
                                strokeDasharray: 2000,
                                strokeDashoffset: 2000,
                            }}
                        />

                        {/* Data Markers */}
                        {[
                            { x: 50, y: 260, year: "2020" },
                            { x: 275, y: 236, year: "2021" },
                            { x: 500, y: 197, year: "2022" },
                            { x: 725, y: 136, year: "2023" },
                            { x: 950, y: 48, year: "Est." }
                        ].map((point, idx) => (
                            <g key={point.year}>
                                <circle
                                    cx={point.x}
                                    cy={point.y}
                                    r="6"
                                    fill="white"
                                    stroke="hsl(var(--primary))"
                                    strokeWidth="3"
                                    className="animate-in zoom-in duration-300 fill-mode-both"
                                    style={{ animationDelay: `${700 + idx * 150}ms` }}
                                />
                                <text x={point.x} y="305" textAnchor="middle" className="text-[14px] fill-muted-foreground font-semibold">
                                    {point.year}
                                </text>
                            </g>
                        ))}
                    </svg>
                </div>

                {/* Legend */}
                <div className="mt-8 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-primary"></div>
                            <span className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Revenue</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-muted"></div>
                        <span className="text-xs text-muted-foreground font-bold uppercase tracking-wider">Benchmark</span>
                    </div>
                </div>

                <style dangerouslySetInnerHTML={{
                    __html: `
                    @keyframes drawLines {
                        to {
                            stroke-dashoffset: 0;
                        }
                    }
                `}} />
            </Card>

            {/* Detailed Metrics */}
            <div className="grid grid-cols-1 gap-4">
                <Card className="p-5 border-l-4 border-primary">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Annual Revenue</h3>
                        <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-none font-bold text-[10px]">+12% YoY</Badge>
                    </div>
                    <div className="text-2xl font-bold mb-1">$840,000</div>
                    <p className="text-xs text-muted-foreground">Last 12 months (LTM)</p>
                </Card>

                <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 border border-border">
                        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Net Margin</h3>
                        <div className="text-xl font-bold">32.5%</div>
                        <div className="flex items-center text-[10px] text-green-600 dark:text-green-400 mt-1">
                            <TrendingUp className="w-3 h-3 mr-0.5" />
                            <span>High Perf.</span>
                        </div>
                    </Card>
                    <Card className="p-4 border border-border">
                        <h3 className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Growth Proj.</h3>
                        <div className="text-xl font-bold">18%</div>
                        <div className="flex items-center text-[10px] text-muted-foreground mt-1">
                            <span>CAGR (5yr)</span>
                        </div>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default FinancialsTab;
